import * as React from 'react';
import { ArrowRight, FolderOpen, Pencil, Plus, Search, Trash2, WrapText } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  BulkBar,
  CitationGrid,
  CommandPaletteTrigger,
  FilterChips,
  GridIconButton,
  KeyHint,
  PanelSection,
  SearchField,
  SegmentedControl,
  Sheet,
  SheetStat,
  SidePanel,
  TreeRail,
  TriCheckbox,
  WorkspaceBanner,
  WorkspaceDataGrid,
  WorkspaceHeader,
  WorkspaceLayout,
  useCommandPalette,
  useRegisterCommands,
  useToast,
  useWorkspacePanel,
  type PaletteCommand,
  type TreeRailCategory,
  type TriCheckboxState,
  type WorkspaceGridColumn,
} from './index';
import { PageCarousel, type CarouselPage } from '../page-carousel';

/**
 * Live examples for the T-0140 workspace patterns, mounted by the
 * `/components` showcase route. Everything here is made-up data held in
 * component state; nothing calls the API.
 */

// ---------------------------------------------------------------------------
// Made-up data: 2,000 rows across 12 modules in 3 categories.
// ---------------------------------------------------------------------------

type Origin = 'library' | 'custom';
type Criticality = 'critical' | 'important' | 'nice';

interface DemoRow {
  id: string;
  no: string;
  module: string;
  sub: string;
  text: string;
  origin: Origin;
  criticality: Criticality;
  locked: boolean;
}

const CATEGORIES: Array<{ id: string; label: string; modules: string[] }> = [
  {
    id: 'finance',
    label: 'Finance',
    modules: ['General Ledger', 'Accounts Payable', 'Budgeting', 'Purchasing'],
  },
  { id: 'hr', label: 'Human resources', modules: ['Payroll', 'Benefits', 'Time & Attendance'] },
  {
    id: 'ops',
    label: 'Operations',
    modules: ['Asset Management', 'Work Orders', 'Inventory', 'Fleet', 'Permitting'],
  },
];
const SUBS = ['General', 'Reporting', 'Integration', 'Security'];
const VERBS = ['record', 'validate', 'route', 'report on', 'reconcile', 'import', 'export'];
const OBJECTS = [
  'journal entries',
  'vendor invoices',
  'purchase requisitions',
  'employee timesheets',
  'fixed assets',
  'budget amendments',
  'approval workflows',
];
const QUALIFIERS = [
  'with a full audit trail',
  'by fund, department and project',
  'without manual re-keying, including attachments and supporting documentation supplied by the originating department',
  'on a configurable schedule',
  'subject to role-based approval limits',
];
const CRIT: Criticality[] = ['critical', 'important', 'nice'];
const EMPTY_MODULE = 'Permitting';

function makeRows(total: number): DemoRow[] {
  const modules = CATEGORIES.flatMap((c) => c.modules).filter((m) => m !== EMPTY_MODULE);
  const rows: DemoRow[] = [];
  for (let i = 0; i < total; i += 1) {
    const module = modules[Math.floor((i * modules.length) / total)]!;
    const n = i + 1;
    rows.push({
      id: `r${n}`,
      no: `R-${String(n).padStart(4, '0')}`,
      module,
      sub: SUBS[Math.floor(i / 12) % SUBS.length]!,
      text: `The system shall ${VERBS[i % VERBS.length]} ${OBJECTS[(i * 3) % OBJECTS.length]} ${QUALIFIERS[(i * 5) % QUALIFIERS.length]}.`,
      origin: i % 9 === 0 ? 'custom' : 'library',
      criticality: CRIT[i % CRIT.length]!,
      locked: i % 37 === 5,
    });
  }
  // Keep each module's rows contiguous by sub-category for grouping.
  return rows.sort(
    (a, b) =>
      modules.indexOf(a.module) - modules.indexOf(b.module) ||
      SUBS.indexOf(a.sub) - SUBS.indexOf(b.sub),
  );
}

const ALL_ID = '__all__';
const CRIT_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'important', label: 'Important' },
  { value: 'nice', label: 'Nice to have' },
] as const;

const BTN =
  'inline-flex h-[30px] items-center gap-1.5 rounded-card border px-2.5 text-meta font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-2';
const BTN_SECONDARY = cn(BTN, 'border-ink-200 bg-paper-0 text-ink-700 hover:bg-paper-50');
const BTN_PRIMARY = cn(
  BTN,
  'border-accent-amber-600 bg-accent-amber-600 text-paper-0 hover:border-accent-amber-500 hover:bg-accent-amber-500',
);

const getRowId = (row: DemoRow) => row.id;
const groupBy = (row: DemoRow) => `${row.module} / ${row.sub}`;
const isRowLocked = (row: DemoRow) => row.locked;

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Needs `ToastProvider` and `CommandPaletteProvider` above it. Both are
 * mounted once at the app root (`routes/__root.tsx`, T-0141); mounting a
 * second pair here would open two palettes on one Cmd/Ctrl+K.
 */
export function WorkspaceShowcase() {
  return (
    <>
      <WorkspaceExample />
      <PageCarouselExample />
      <PrimitiveExamples />
    </>
  );
}

function ShowcaseCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-card border border-ink-100 bg-paper-0 p-6">
      <div>
        <h2 className="text-h2 font-semibold text-ink-900">{title}</h2>
        <p className="mt-1 text-body-sm text-ink-500">{description}</p>
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The composed example: layout + rail + grid + panel + bulk bar + palette
// ---------------------------------------------------------------------------

function WorkspaceExample() {
  const { toast } = useToast();
  const { setOpen: setPaletteOpen } = useCommandPalette();
  const [rows, setRows] = React.useState<DemoRow[]>(() => makeRows(2000));
  const [activeId, setActiveId] = React.useState<string>(ALL_ID);
  const [query, setQuery] = React.useState('');
  const [origin, setOrigin] = React.useState<'all' | Origin>('all');
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [panelId, setPanelId] = React.useState<string | null>(null);
  const [wrap, setWrap] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Instant search: the input updates immediately, the filter follows.
  const deferredQuery = React.useDeferredValue(query);

  const inModule = React.useMemo(
    () => (activeId === ALL_ID ? rows : rows.filter((r) => r.module === activeId)),
    [rows, activeId],
  );
  const visible = React.useMemo(() => {
    const terms = deferredQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return inModule.filter(
      (r) =>
        (origin === 'all' || r.origin === origin) &&
        terms.every((t) => r.text.toLowerCase().includes(t) || r.no.toLowerCase().includes(t)),
    );
  }, [inModule, origin, deferredQuery]);

  const categories = React.useMemo<TreeRailCategory[]>(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.module, (counts.get(r.module) ?? 0) + 1);
    return CATEGORIES.map((c) => ({
      id: c.id,
      label: c.label,
      count: c.modules.reduce((sum, m) => sum + (counts.get(m) ?? 0), 0),
      items: c.modules.map((m) => ({
        id: m,
        label: m,
        count: counts.get(m) ?? 0,
        dot: m === 'Budgeting',
        dotLabel: 'Draft in progress',
      })),
    }));
  }, [rows]);

  // Latest rows for callbacks that must stay stable for the grid.
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  const applyWithUndo = React.useCallback(
    (next: DemoRow[], message: string) => {
      const before = rowsRef.current;
      setRows(next);
      toast({ message, actions: [{ label: 'Undo', onClick: () => setRows(before) }] });
    },
    [toast],
  );

  const onEditCommit = React.useCallback(
    (rowId: string, _columnKey: string, value: string) => {
      const text = value.trim();
      if (!text) return;
      const before = rowsRef.current;
      const target = before.find((r) => r.id === rowId);
      applyWithUndo(
        before.map((r) => (r.id === rowId ? { ...r, text } : r)),
        `${target?.no ?? 'Row'} updated`,
      );
    },
    [applyWithUndo],
  );

  const removeRows = (ids: ReadonlySet<string>) => {
    applyWithUndo(
      rows.filter((r) => !ids.has(r.id)),
      `${ids.size.toLocaleString()} removed`,
    );
    setSelected(new Set());
    if (panelId && ids.has(panelId)) setPanelId(null);
  };

  const columns = React.useMemo<WorkspaceGridColumn<DemoRow>[]>(
    () => [
      {
        key: 'no',
        header: 'No.',
        width: '84px',
        cell: (r) => <span className="font-mono text-meta font-medium text-ink-700">{r.no}</span>,
      },
      {
        key: 'text',
        header: 'Requirement',
        cell: (r) => <span className="text-ink-900">{r.text}</span>,
        editValue: (r) => r.text,
        editLabel: 'Requirement text',
      },
      {
        key: 'criticality',
        header: 'Criticality',
        width: '132px',
        text: false,
        cell: (r) => (
          <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700">
            {CRIT_OPTIONS.find((o) => o.value === r.criticality)?.label}
          </span>
        ),
      },
      {
        key: 'origin',
        header: 'Origin',
        width: '120px',
        cell: (r) => (
          <span className="text-meta text-ink-500">
            {r.origin === 'library' ? 'Library' : 'Custom'}
          </span>
        ),
      },
    ],
    [],
  );

  const rowActions = React.useCallback(
    (r: DemoRow) => (
      <GridIconButton aria-label={`Open ${r.no}`} onClick={() => setPanelId(r.id)}>
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </GridIconButton>
    ),
    [],
  );

  const openRow = React.useCallback((id: string) => setPanelId(id), []);
  // Like the prototype: once the panel is open it follows row focus.
  const focusRow = React.useCallback((id: string) => {
    setFocusedId(id);
    setPanelId((open) => (open ? id : open));
  }, []);

  // Commands this "screen" contributes to the palette.
  const commands = React.useMemo<PaletteCommand[]>(
    () => [
      ...CATEGORIES.flatMap((c) => c.modules).map((m) => ({
        id: `module:${m}`,
        group: 'Modules',
        label: m,
        icon: <FolderOpen className="h-3.5 w-3.5" />,
        run: () => setActiveId(m),
      })),
      {
        id: 'action:add',
        group: 'Actions',
        label: 'Add from library',
        icon: <Plus className="h-3.5 w-3.5" />,
        run: () => setSheetOpen(true),
      },
      {
        id: 'action:wrap',
        group: 'Actions',
        label: 'Toggle wrap text',
        icon: <WrapText className="h-3.5 w-3.5" />,
        keywords: ['ellipsis', 'lines'],
        run: () => setWrap((w) => !w),
      },
      {
        id: 'action:clear',
        group: 'Actions',
        label: 'Clear selection',
        run: () => setSelected(new Set()),
      },
    ],
    [],
  );
  useRegisterCommands(commands);

  const panelRow = panelId ? (rows.find((r) => r.id === panelId) ?? null) : null;
  const selectableVisible = visible.filter((r) => !r.locked).length;

  return (
    <ShowcaseCard
      title="Workspace patterns"
      description="WorkspaceLayout + TreeRail + WorkspaceDataGrid (2,000 made-up rows, virtualised) + SidePanel + BulkBar + FilterChips + command palette + toast with undo. Click the grid, then use j / k, Enter, x, e. Press Cmd/Ctrl+K for the palette. The panel docks at viewport widths of 1500px and up and floats below that."
    >
      <div className="h-[680px] overflow-hidden rounded-card border border-ink-100">
        <WorkspaceLayout
          header={
            <WorkspaceHeader
              title="Requirements (example)"
              meta={`${rows.length.toLocaleString()} rows of made-up data`}
              actions={
                <>
                  <CommandPaletteTrigger />
                  <button
                    type="button"
                    aria-pressed={wrap}
                    onClick={() => setWrap((w) => !w)}
                    className={BTN_SECONDARY}
                  >
                    <WrapText className="h-3.5 w-3.5" aria-hidden="true" />
                    Wrap text
                  </button>
                  <button type="button" onClick={() => setSheetOpen(true)} className={BTN_PRIMARY}>
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    Add from library
                  </button>
                </>
              }
            />
          }
          banner={
            <WorkspaceBanner
              actions={
                <button
                  type="button"
                  className={BTN_SECONDARY}
                  onClick={() => setPaletteOpen(true)}
                >
                  Open palette
                </button>
              }
            >
              <strong className="font-semibold text-ink-900">Banner slot.</strong> Budgeting has a
              draft in progress (the amber dot in the rail).
            </WorkspaceBanner>
          }
          railLabel="Modules"
          rail={
            <TreeRail
              aria-label="Modules"
              all={{ id: ALL_ID, label: 'All requirements', count: rows.length }}
              categories={categories}
              activeId={activeId}
              onSelect={setActiveId}
              dimZero
              defaultCollapsedIds={['ops']}
            />
          }
          toolbar={
            <>
              <SearchField
                ref={searchRef}
                value={query}
                onChange={setQuery}
                aria-label="Search requirements"
                placeholder="Search requirements…"
                icon={<Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />}
              />
              <FilterChips
                aria-label="Filter by origin"
                value={origin}
                onChange={setOrigin}
                options={[
                  { value: 'all', label: 'All', count: inModule.length },
                  {
                    value: 'library',
                    label: 'Library',
                    count: inModule.filter((r) => r.origin === 'library').length,
                  },
                  {
                    value: 'custom',
                    label: 'Custom',
                    count: inModule.filter((r) => r.origin === 'custom').length,
                  },
                ]}
              />
              <span className="flex-1" />
              <span className="whitespace-nowrap text-meta text-ink-500">
                {visible.length.toLocaleString()} shown
              </span>
            </>
          }
          footer={
            <>
              <KeyHint keys={['j', 'k']}>move</KeyHint>
              <KeyHint keys={['Enter']}>open</KeyHint>
              <KeyHint keys={['x']}>select</KeyHint>
              <KeyHint keys={['Shift', 'click']}>select a range</KeyHint>
              <KeyHint keys={['e']}>edit text</KeyHint>
              <KeyHint keys={['Esc']}>close</KeyHint>
            </>
          }
          overlay={
            <BulkBar
              count={selected.size}
              noun="requirement"
              selectAll={{
                total: selectableVisible,
                onSelectAll: () =>
                  setSelected(new Set(visible.filter((r) => !r.locked).map((r) => r.id))),
              }}
              actions={[
                {
                  id: 'remove',
                  label: 'Remove',
                  icon: <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />,
                  onClick: () => removeRows(selected),
                },
              ]}
              onClear={() => setSelected(new Set())}
            />
          }
          panel={
            panelRow ? (
              <DemoPanel
                row={panelRow}
                onClose={() => setPanelId(null)}
                onCriticality={(c) =>
                  setRows((prev) =>
                    prev.map((r) => (r.id === panelRow.id ? { ...r, criticality: c } : r)),
                  )
                }
              />
            ) : null
          }
        >
          <WorkspaceDataGrid
            aria-label="Requirements"
            rows={visible}
            getRowId={getRowId}
            columns={columns}
            groupBy={groupBy}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            isRowLocked={isRowLocked}
            focusedId={focusedId}
            onFocusChange={focusRow}
            onOpen={openRow}
            onEditCommit={onEditCommit}
            wrap={wrap}
            rowActions={rowActions}
            emptyState={
              <div className="px-6 py-14 text-center text-body-sm text-ink-500">
                <strong className="mb-1 block text-h3 font-semibold text-ink-900">
                  Nothing matches
                </strong>
                Clear the search or pick another module.
              </div>
            }
          />
        </WorkspaceLayout>
      </div>

      <DemoSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </ShowcaseCard>
  );
}

function DemoPanel({
  row,
  onClose,
  onCriticality,
}: {
  row: DemoRow;
  onClose: () => void;
  onCriticality: (c: Criticality) => void;
}) {
  const { mode } = useWorkspacePanel();
  return (
    <SidePanel
      aria-label={`Requirement ${row.no}`}
      identifier={row.no}
      path={`${row.module} / ${row.sub}`}
      onClose={onClose}
      footer={
        <>
          <span className="text-meta text-ink-500">Panel is {mode}</span>
          <button type="button" className={BTN_SECONDARY} onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <PanelSection label="Requirement">
        <p className="text-body text-ink-900">{row.text}</p>
      </PanelSection>
      <PanelSection label="Criticality">
        <SegmentedControl
          aria-label="Criticality"
          options={CRIT_OPTIONS}
          value={row.criticality}
          onChange={onCriticality}
          readOnly={row.locked}
        />
      </PanelSection>
      <PanelSection label="Source">
        <CitationGrid
          items={[
            { label: 'Origin', value: row.origin === 'library' ? 'Firm library' : 'Added here' },
            { label: 'Module', value: row.module },
            { label: 'Library ref', value: `LIB-${row.no.slice(2)}`, mono: true },
            { label: 'Version', value: 'v3', mono: true },
          ]}
        />
      </PanelSection>
      <PanelSection label="Source (AI-derived)">
        <CitationGrid
          tone="ai"
          items={[
            { label: 'Document', value: 'Discovery session notes.docx', mono: true },
            { label: 'Location', value: 'Page 4, paragraph 2' },
          ]}
        />
      </PanelSection>
    </SidePanel>
  );
}

const SHEET_ITEMS = Array.from({ length: 8 }, (_, i) => ({
  id: `s${i}`,
  no: `LIB-${String(i + 1).padStart(4, '0')}`,
  text: `The system shall ${VERBS[i % VERBS.length]} ${OBJECTS[i % OBJECTS.length]}.`,
  locked: i === 1,
}));

function DemoSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set());
  const [strict, setStrict] = React.useState(false);
  const pickable = SHEET_ITEMS.filter((i) => !i.locked);
  const allState: TriCheckboxState =
    picked.size === 0 ? 'off' : picked.size === pickable.length ? 'on' : 'mixed';
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Add from library"
      description="Sheet: header, body, footer with a running summary and actions."
      closeOnScrimClick={!strict}
      summary={
        <>
          <SheetStat value={picked.size} label="selected" />
          <SheetStat value={SHEET_ITEMS.length - pickable.length} label="already included" />
        </>
      }
      actions={
        <>
          <button type="button" className={BTN_SECONDARY} onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={cn(BTN_PRIMARY, 'disabled:cursor-not-allowed disabled:opacity-50')}
            disabled={picked.size === 0}
            onClick={() => onOpenChange(false)}
          >
            Add {picked.size || ''} to engagement
          </button>
        </>
      }
    >
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="flex items-center gap-2.5 border-b border-ink-100 px-5 py-3 text-body-sm font-semibold text-ink-900">
          <TriCheckbox
            state={allState}
            aria-label="Select all in this module"
            onChange={(next) => setPicked(next ? new Set(pickable.map((i) => i.id)) : new Set())}
          />
          General Ledger
          <label className="ml-auto flex items-center gap-2 text-meta font-medium text-ink-500">
            <input type="checkbox" checked={strict} onChange={(e) => setStrict(e.target.checked)} />
            Keep open on scrim click
          </label>
        </div>
        {SHEET_ITEMS.map((item) => (
          <div
            key={item.id}
            className="grid grid-cols-[16px_72px_1fr] items-start gap-3 border-b border-ink-100 px-5 py-2 text-body-sm"
          >
            <TriCheckbox
              className="mt-0.5"
              state={item.locked ? 'locked' : picked.has(item.id) ? 'on' : 'off'}
              aria-label={item.locked ? `${item.no} already included` : `Select ${item.no}`}
              onChange={(next) =>
                setPicked((prev) => {
                  const s = new Set(prev);
                  if (next) s.add(item.id);
                  else s.delete(item.id);
                  return s;
                })
              }
            />
            <span className="font-mono text-meta font-medium text-ink-700">{item.no}</span>
            <span className={item.locked ? 'text-ink-500' : 'text-ink-900'}>{item.text}</span>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Small primitives on their own
// ---------------------------------------------------------------------------

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-4 border-t border-ink-100 py-3 first:border-t-0 first:pt-0">
      <span className="text-meta">{label}</span>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

function PrimitiveExamples() {
  const { toast } = useToast();
  const [check, setCheck] = React.useState<TriCheckboxState>('off');
  const [chip, setChip] = React.useState<'all' | 'open' | 'done'>('all');
  const [seg, setSeg] = React.useState<Criticality>('important');
  const [undone, setUndone] = React.useState(0);

  return (
    <ShowcaseCard
      title="Workspace primitives"
      description="The smaller building blocks on their own: TriCheckbox, FilterChips, SegmentedControl, CitationGrid, toast with undo."
    >
      <Row label="TriCheckbox">
        {(['off', 'on', 'mixed', 'locked'] as const).map((s) => (
          <span key={s} className="inline-flex items-center gap-2 text-body-sm text-ink-700">
            <TriCheckbox state={s} aria-label={`Example: ${s}`} />
            {s}
          </span>
        ))}
        <span className="inline-flex items-center gap-2 text-body-sm text-ink-700">
          <TriCheckbox
            state={check}
            aria-label="Interactive example"
            onChange={(next) => setCheck(next ? 'on' : 'off')}
          />
          interactive
          <button type="button" className={BTN_SECONDARY} onClick={() => setCheck('mixed')}>
            Set mixed
          </button>
        </span>
      </Row>
      <Row label="FilterChips">
        <FilterChips
          aria-label="Example filter"
          value={chip}
          onChange={setChip}
          options={[
            { value: 'all', label: 'All', count: 1284 },
            { value: 'open', label: 'Open', count: 37 },
            { value: 'done', label: 'Done' },
          ]}
        />
      </Row>
      <Row label="Segmented">
        <SegmentedControl
          aria-label="Criticality"
          options={CRIT_OPTIONS}
          value={seg}
          onChange={setSeg}
        />
        <SegmentedControl
          aria-label="Criticality (read only)"
          options={CRIT_OPTIONS}
          value={seg}
          readOnly
        />
      </Row>
      <Row label="CitationGrid">
        <CitationGrid
          className="w-[380px]"
          items={[
            { label: 'Origin', value: 'Firm library' },
            { label: 'Library ref', value: 'LIB-0042', mono: true },
          ]}
        />
      </Row>
      <Row label="Toast">
        <button type="button" className={BTN_SECONDARY} onClick={() => toast({ message: 'Saved' })}>
          Plain toast
        </button>
        <button
          type="button"
          className={BTN_SECONDARY}
          onClick={() =>
            toast({
              message: '3 requirements removed',
              actions: [
                { label: 'View', onClick: () => {} },
                { label: 'Undo', onClick: () => setUndone((n) => n + 1) },
              ],
            })
          }
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          Toast with undo
        </button>
        <span className="text-meta text-ink-500">Undo clicked {undone} times</span>
      </Row>
    </ShowcaseCard>
  );
}

// ---------------------------------------------------------------------------
// PageCarousel (T-0282): the report's eleven pages, made up
// ---------------------------------------------------------------------------

const CAROUSEL_PAGES: CarouselPage[] = [
  { key: 'cover', title: 'Cover', origin: 'ai' },
  { key: 'summary', title: 'Executive summary', origin: 'edited' },
  { key: 'told_us', title: 'What the City told us', origin: 'code' },
  { key: 'requirements', title: 'Requirements', origin: 'code' },
  { key: 'matrix', title: 'Fit matrix', origin: 'code' },
  { key: 'ranking', title: 'Ranking', origin: 'ai' },
  { key: 'vendor:tyler', title: 'Tyler', origin: 'edited' },
  { key: 'vendor:opengov', title: 'OpenGov', origin: 'ai', issues: 1 },
  { key: 'vendor:springbrook', title: 'Springbrook', origin: 'ai' },
  { key: 'set_aside', title: 'Set aside', origin: 'code' },
  { key: 'method', title: 'Method', origin: 'ai' },
];

const CAROUSEL_PARAGRAPH =
  'These three vendors rank first, second and third on the evidence in the vendor knowledge base, weighted by the requirements in the draft RTM. Each answers a different part of what staff described in discovery, and each carries a risk the demonstrations must settle.';

function PageCarouselExample() {
  const [current, setCurrent] = React.useState('summary');
  const at = CAROUSEL_PAGES.findIndex((p) => p.key === current);
  return (
    <ShowcaseCard
      title="Page carousel"
      description="PageCarousel: pages moved through sideways, one per screen, each with its own vertical scroll. The strip shows the page number and title, an origin dot (AI draft, edited) and an issue count. Move with the strip, the buttons, the arrow keys, or a swipe. The strip is a select below 640px. Page 4 is taller than the card."
    >
      <div className="h-[560px]">
        <PageCarousel
          aria-label="Report pages (example)"
          pages={CAROUSEL_PAGES}
          current={current}
          onChange={setCurrent}
          header={
            <div className="flex flex-wrap items-center gap-2 text-body-sm text-ink-500">
              <span>
                Page {at + 1} of {CAROUSEL_PAGES.length}: {CAROUSEL_PAGES[at]?.title}
              </span>
              <span className="flex-1" />
              <span className="text-meta">Header slot</span>
            </div>
          }
          renderPage={(page, i) => (
            <div className="flex flex-col gap-3 p-6">
              <span className="text-meta font-medium uppercase tracking-wide text-ink-500">
                Page {i + 1} of {CAROUSEL_PAGES.length}
              </span>
              <h3 className="text-h2 font-semibold text-ink-900">{page.title}</h3>
              {Array.from({ length: page.key === 'requirements' ? 14 : 3 }, (_, n) => (
                <p key={n} className="text-body text-ink-700">
                  {CAROUSEL_PARAGRAPH}
                </p>
              ))}
            </div>
          )}
        />
      </div>
    </ShowcaseCard>
  );
}
